import { useState, useEffect, useMemo } from "react";
import { Feature, Geometry, GeoJsonProperties } from "geojson";
import { Map, MapboxGeoJSONFeature } from "mapbox-gl";
import {
  CmsMapData,
  CmsMapDataLayerNode,
  useCmsMapData,
} from "./useCmsMapData";
import { MapTypes, mapTypes } from "../mapConfig";
import { MapTypeData } from "../map.interfaces";
import { useMapboxData } from "./useMapboxData";

export interface SearchData {
  title: string;
  cmsNodeData: CmsMapDataLayerNode | null;
  mapboxFeatureData: Feature<Geometry, GeoJsonProperties>;
}

export const useMapData = (
  map: Map | null,
  mapId?: string | undefined,
  locale?: string | null
) => {
  const { cmsMapData } = useCmsMapData(locale || null);
  const [currentCmsMapData, setCurrentCmsMapData] = useState<CmsMapData | null>(
    null
  );
  const [mapConfig, setMapConfig] = useState<MapTypeData>();
  const [currentMapId, setCurrentMapId] = useState<string>();
  const [mapLinkedWithPage, setMapLinkedWithPage] = useState(false);

  // -------------------------------------------------------
  // USE EFFECT - check for cmsMapData loaded from Optimizely
  // -------------------------------------------------------

  useEffect(() => {
    if (cmsMapData) {
      // Remove any ending forward slashes from the pathName
      const pathName = window.location.pathname.replace(/\/+$/, '');
      const splitPath = pathName.split("/");

      const currentPage = splitPath[splitPath.length - 1];
      const pageNameMatch = cmsMapData.find(
        (cmsData) => cmsData.pageName.toLowerCase() === currentPage.toLowerCase() ||
          cmsData.simpleAddress.toLowerCase() === currentPage.toLowerCase() 
      );
      const data =
        cmsMapData.find((cmsData) => cmsData.mapId === mapId) ||
        pageNameMatch ||
        cmsMapData[0];

      setCurrentCmsMapData(data);
      setCurrentMapId(data.mapId);
    }
  }, [cmsMapData]);

  useEffect(() => {
    if (currentCmsMapData) {
      // Remove any ending forward slashes from the pathName
      const pathName = window.location.pathname.replace(/\/+$/, '');
      const splitPath = pathName.split("/");

      const currentPage = splitPath[splitPath.length - 1];
      const pageNameMatch = (
        currentCmsMapData.pageName.toLowerCase() === currentPage.toLowerCase() ||
        currentCmsMapData.simpleAddress.toLowerCase() === currentPage.toLowerCase()
      )

      setMapLinkedWithPage(pageNameMatch);
    }
  }, [currentCmsMapData]);

  // -----------------------------------------------------------------------
  // USE EFFECT - check for mapId change -> load map based on  new mapId
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (cmsMapData && currentMapId !== mapId) {
      const data =
        cmsMapData.find((cmsData) => cmsData.mapId === mapId) || cmsMapData[0];
      map = null;

      setCurrentCmsMapData(data);
      setCurrentMapId(data.mapId);
    }
  }, [mapId]);

  useEffect(() => {
    if (map) {
      setMapConfig({
        longitude: map.getCenter().lng,
        latitude: map.getCenter().lat,
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      });
    }
  }, [mapId, map]);

  // -----------------------------------------------------------------------
  // USE MEMO - set customMapLayer when map changes - maps made by GHAB
  // -----------------------------------------------------------------------

  const customMapLayers = useMemo(() => {
    if (map) {
      const mapLayers = map.getStyle().layers;
      if (mapLayers) {
        const filteredGhabLayers = mapLayers.filter((layer) =>
          layer.id.startsWith("ghab")
        );
        return filteredGhabLayers;
      }
    }
    return [];
  }, [map]);

  // -----------------------------------------------------------------------
  // USE MAPBOX DATA - trigger useMapboxData hook and get nodeData
  // -----------------------------------------------------------------------

  const { mapBoxFeatures } = useMapboxData(customMapLayers, currentCmsMapData);

  // -----------------------------------------------------------------------
  // GET TITLE - this method returns a title based on the body
  //             text from Optimizely
  // -----------------------------------------------------------------------

  const getTitle = (body: string | undefined) => {
    if (body) {
      const headerTags = ["h1", "h2", "h3", "h4", "h5", "h6"];
      const headerTexts: string[] = [];
      headerTags.forEach((tag) => {
        const startIndex = body.indexOf(`<${tag}>`);
        const endIndex = body.indexOf(`</${tag}>`);
        const headerHtml = body.substring(startIndex + 4, endIndex);
        const el: HTMLElement = document.createElement(tag);
        el.innerHTML = headerHtml;

        if (startIndex >= 0) {
          headerTexts.push(el.innerText);
        }
      });

      return headerTexts[0];
    }
  };

  // -----------------------------------------------------------------------
  // USE MEMO - set searchData when customMapLayers and
  //            currentCmsMapData changes
  // -----------------------------------------------------------------------

  const searchData = useMemo(() => {
    if (map && customMapLayers && currentCmsMapData) {
      const searchDataList: SearchData[] = [];

      // Generate search data of linear service GeoJSON features
      if (currentCmsMapData.mapType === MapTypes.LINER_SERVICE_MAP) {
        mapBoxFeatures?.forEach((feature) => {
          const title =
            (feature.properties as any)[`name_${locale}`] ||
            (feature.properties as any)[`name_en`];

          searchDataList.push({
            title,
            cmsNodeData: null,
            mapboxFeatureData: feature,
          });
        });
      } else {
        currentCmsMapData.mapLayers.forEach((cmsMapLayer) => {
          cmsMapLayer.mapNodes?.forEach((cmsMapNode) => {
            const mapboxFeature = mapBoxFeatures?.find(
              (feature) =>
                String(feature.properties?.nodeId) === cmsMapNode.nodeId
            );
            if (mapboxFeature) {
              searchDataList.push({
                title: getTitle(cmsMapNode.body) || cmsMapNode.nodeId,
                cmsNodeData: cmsMapNode,
                mapboxFeatureData: mapboxFeature,
              });
            }
          });
        });
      }

      return searchDataList || [];
    }
  }, [customMapLayers, currentCmsMapData, mapBoxFeatures]);

  // -----------------------------------------------------------------------
  // RETURN
  // -----------------------------------------------------------------------

  return {
    cmsMapData,
    currentCmsMapData,
    customMapLayers,
    mapConfig,
    searchData,
    mapLinkedWithPage,
  };
};
